import React, { useEffect, useRef, useState } from "react";
import EventCard from "../../components/events/EventCard";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useTranslation } from "react-i18next";
import { buildEventPath, isPlainLeftClick } from "../../utils/eventUrl";
import { trackEventEssentialAnalytics } from "../../utils/essentialAnalytics";
import { BADGES } from "../../constants/badges";
import EventMetaIcon from "./EventMetaIcon";
import { getEventWeekdayAbbr } from "../../utils/eventDateLabels";
import { resolveAvatarMediaSrc, resolveUserAvatarSrc } from "../../utils/userAvatar";
import { DELETED_USER_LABEL } from "../../utils/deletedAccount";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { isConceptAwaitingDetails } from "./conceptPresentation";

const pad2 = (n) => String(n).padStart(2, "0");
const formatDatetimeForCard = (input, language) => {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input ?? "");
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())} ${pad2(d.getDate())}/${pad2(
    d.getMonth() + 1
  )} ${getEventWeekdayAbbr(d.getDay(), language)}`;
};
const formatDateOnlyForCard = (input) => {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input ?? "");
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
};

/* — вспомогалка: ищем ближайший скроллируемый элемент — */
const getScrollParent = (node) => {
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return window;                 // на всякий случай
};

const SectionSeparator = ({ label }) => (
  <div className="flex items-center my-2">
    <div className="flex-grow h-px bg-white/20" />
    {label ? (
      <span className="mx-3 text-xs font-semibold tracking-[0.16em] uppercase text-white/60">
        {label}
      </span>
    ) : null}
    <div className="flex-grow h-px bg-white/20" />
  </div>
);

const ConceptsDivider = ({ label, compact = false }) => (
  <div className={`${compact ? "my-0" : "my-5"} flex justify-center`}>
    <div className="flex h-[25px] w-full max-w-[354px] items-center justify-center rounded-[50px] border border-white/35 px-4">
      <span className="text-[12px] font-bold uppercase leading-[22px] tracking-[0.02em] text-white/55">
        {label}
      </span>
    </div>
  </div>
);

const CONCEPT_AUTHOR_FALLBACK_AVATAR = "/assets/avatars/paper-cap.webp";
const CONCEPT_VAULTGRID_AVATAR = "/assets/avatars/vaultgrid.png";

const ConceptPostIntro = ({ event, onOpenAuthor }) => {
  const { t } = useTranslation("events");
  const isHostlessPost = Boolean(event?.is_hostless_concept);
  const isClubPost = !isHostlessPost && Number(event?.host_format) === 1 && Boolean(event?.club);
  const author = event?.concept_author || {};
  const isDeletedAuthor = !isHostlessPost && !isClubPost && Boolean(author.is_deleted_account);
  const avatarSrc = isHostlessPost
    ? CONCEPT_VAULTGRID_AVATAR
    : isClubPost
      ? resolveAvatarMediaSrc(event.club?.hero_image_url)
      : resolveUserAvatarSrc(author);
  const authorCode = isClubPost
      ? t("event_description_modal.chips.club_host_short", { defaultValue: "Club" })
      : author.code || "—";
  const authorName = isHostlessPost ? "VaultGrid Idea" : isClubPost ? event.club.name : "";
  const canOpenAuthor = Boolean(onOpenAuthor) && (isClubPost
    ? Boolean(event.club?.public_path || event.club?.id || event.club_id)
    : !isHostlessPost && !isDeletedAuthor && Boolean(author.code));
  const isPremiumAuthor = !isHostlessPost && !isClubPost && Boolean(author.has_premium_subscription);
  const hasCustomAvatar = isHostlessPost || isClubPost || Boolean(author.avatar_custom_url);
  const description = String(event?.description_preview || event?.description || event?.name || "")
    .replace(/\s+/g, " ")
    .trim();
  const avatar = (
    <img
      src={avatarSrc || CONCEPT_AUTHOR_FALLBACK_AVATAR}
      alt={canOpenAuthor ? "" : isHostlessPost ? "VaultGrid Idea" : authorCode}
      onError={(error) => {
        if (!error.currentTarget.src.endsWith(CONCEPT_AUTHOR_FALLBACK_AVATAR)) {
          error.currentTarget.src = CONCEPT_AUTHOR_FALLBACK_AVATAR;
        }
      }}
      className={`h-[55px] w-[55px] rounded-full object-cover ${
        isPremiumAuthor ? "ring-2 ring-white" : hasCustomAvatar ? "ring-2 ring-white/80" : ""
      }`}
      loading="lazy"
    />
  );

  return (
    <div data-testid="concept-post-intro" className="flex w-full items-start gap-2.5 pb-3 pt-1 text-white">
      <div className="flex w-[55px] shrink-0 flex-col items-center">
        {isDeletedAuthor ? (
          <div
            aria-label={DELETED_USER_LABEL}
            className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
          >
            DU
          </div>
        ) : canOpenAuthor ? (
          <button
            type="button"
            onClick={() => onOpenAuthor(event, isClubPost ? "club" : "user")}
            aria-label={isClubPost
              ? t("menu:chats.group_chat.open_club_aria", { name: event.club?.name, defaultValue: "Open club" })
              : t("buddy_groups.open_profile", { name: authorCode, defaultValue: "Open profile" })}
            className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            {avatar}
          </button>
        ) : avatar}
        {isDeletedAuthor ? (
          <span className="mt-[5px] w-full truncate text-center text-[11px] font-bold leading-[14px] text-white/75">
            {DELETED_USER_LABEL}
          </span>
        ) : isHostlessPost ? null : (
          <PremiumCodeLabel
            as="div"
            code={authorCode}
            isPremium={isPremiumAuthor}
            className="mt-[5px] max-w-full truncate text-center text-[20px] font-black leading-[22px] text-white"
            outlineClassName="rounded-[4px] px-[3px] py-0"
          />
        )}
      </div>
      <div className="min-w-0 flex-1 pt-[2px]">
        {authorName ? (
          <p className="mb-1 truncate text-[18px] font-semibold leading-[22px] text-white">
            {authorName}
          </p>
        ) : null}
        <p className="line-clamp-4 break-words text-[14px] leading-[19px] text-white/90 [overflow-wrap:anywhere]">
          {description}
        </p>
      </div>
    </div>
  );
};

const getBadgeSrc = (label) => {
  const badge = BADGES.find((item) => item.id === label);
  return badge?.src || "/assets/event_pics/default.webp";
};

const supportsPartySession = (event) =>
  Boolean(
    event?.is_vaultgrid_party
    || event?.is_new_joiners_event
    || event?.is_sport_session_event
  );

const isRenderableEvent = (event) =>
  Boolean(event) &&
  typeof event === "object" &&
  event.id !== null &&
  typeof event.id !== "undefined";

const hasViewerConfirmedAttendance = (event) => {
  if (event?.viewer_attended === true || event?.attended === true) return true;
  const storageCode = String(event?.storage_code || "").trim();
  const participants = Array.isArray(event?.participants) ? event.participants : [];
  if (!storageCode || !participants.length) return false;
  return participants.some(
    (participant) =>
      String(participant?.code || "").trim() === storageCode &&
      Boolean(participant?.attended)
  );
};

const InlineSeriesEventRow = ({
  event,
  onSelect,
  onToggleFavourite,
  isFavourite = false,
  datetimeDisplayMode = "date_time",
  titleMaxWidthClass = "",
}) => {
  const { t, i18n } = useTranslation("events");
  const isConcept = event?.type === "concept";
  const fmt =
    isConcept
      ? t("event_card.concept_label", { defaultValue: "Concept" })
      : datetimeDisplayMode === "date_only"
      ? formatDateOnlyForCard(event?.date_time)
      : formatDatetimeForCard(event?.date_time, i18n?.resolvedLanguage || i18n?.language);
  const badgeSrc = String(event?.icon_image_url || "").trim() ||
    (event?.badge_label ? getBadgeSrc(event.badge_label) : null);
  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(event);
  };

  return (
    <div
      className="relative z-10 flex items-start w-full cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleOpen(e);
        }
      }}
    >
      {badgeSrc ? (
        <img
          src={badgeSrc}
          alt={t("event_card.thumbnails.badge_alt", { title: event?.name || "" })}
          className="mr-2 h-[55px] w-[55px] rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div
          className="mr-2 h-[55px] w-[55px] rounded-full flex-shrink-0"
          style={{ backgroundColor: event?.color || "#ffffff" }}
        />
      )}
        <div className="flex-1 flex flex-col justify-between h-[55px]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className={`truncate text-lg font-extrabold text-white translate-y-[2px] ${titleMaxWidthClass}`}>
                {event?.name}
              </div>
            </div>
          <button
            type="button"
            className="ml-1"
            aria-label={t("event_card.actions.favourite_aria")}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavourite?.(event?.id);
            }}
            style={{ transform: "translateY(-2px)" }}
          >
            <img
              src={`/assets/events/${isFavourite ? "like_icon_on.svg" : "like_icon_off.svg"}`}
              alt={t("event_card.icons.like_alt")}
              className="w-5 h-5"
            />
          </button>
        </div>
        <div className="flex items-center justify-between gap-6 min-w-0">
          <div className="flex items-center gap-2">
            <EventMetaIcon
              src={`/assets/events/${Number(event?.event_format) === 0 ? "online_icon.svg" : "offline_icon.svg"}`}
              alt={
                Number(event?.event_format) === 0
                  ? t("event_card.icons.online_alt")
                  : t("event_card.icons.offline_alt")
              }
              className="w-6 h-6"
            />
            <EventMetaIcon
              src={`/assets/events/${Number(event?.host_format) === 0 ? "personal_icon.svg" : "organization_icon.svg"}`}
              alt={
                Number(event?.host_format) === 0
                  ? t("event_card.icons.personal_alt")
                  : t("event_card.icons.organization_alt")
              }
              className="w-6 h-6"
            />
            {Number(event?.fee_type) === 1 ? (
              <EventMetaIcon
                src="/assets/events/payable_icon.svg"
                alt={t("event_card.icons.paid_alt")}
                className="w-6 h-6"
              />
            ) : null}
            {event?.is_verified ? (
              <EventMetaIcon
                src="/assets/events/verified_icon.svg"
                alt={t("event_card.icons.verified_alt")}
                className="w-6 h-6"
              />
            ) : null}
            {event?.join_by_request ? (
              <EventMetaIcon
                src="/assets/events/private_event.svg"
                alt={t("event_card.icons.by_request_alt")}
                className="w-6 h-6"
              />
            ) : null}
          </div>
          <div className="text-sm font-semibold flex-shrink-0 text-white/50 translate-y-[5px]">
            {fmt}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatStartsIn = (targetDate, fallbackLabel) => {
  const ts = targetDate instanceof Date ? targetDate.getTime() : NaN;
  if (!Number.isFinite(ts)) return fallbackLabel;

  const diffMs = ts - Date.now();
  if (diffMs <= 0) return fallbackLabel;

  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
};

const EventRowAnchor = ({
  event,
  href,
  onOpen,
  analyticsSurface,
  analyticsListType,
  analyticsSection,
  analyticsPosition,
  children,
}) => {
  const anchorRef = useRef(null);

  useEffect(() => {
    const node = anchorRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          trackEventEssentialAnalytics({
            eventId: event?.id,
            action: "impression",
            surface: analyticsSurface || "list",
          });
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.6,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [analyticsListType, analyticsPosition, analyticsSection, analyticsSurface, event]);

  return (
    <a
      ref={anchorRef}
      id={`event-${event.id}`}
      href={href}
      onClick={onOpen}
      className="relative overflow-visible block"
    >
      {children}
    </a>
  );
};

const EventList = ({
  events = [],
  onSelect,
  hideMapButton = false,
  onToggleFavourite,
  showFavouriteButton = true,
  favouriteIds,
  selectedEventId,
  modalHeight = 0,
  fromMap = false,
  eventStatuses,
  skipPreviewOnDesktop = false,
  showBottomMargin = true,
  showMainEvent = true,
  disableClientSort = false,
  showUnreadBadges = false,
  unreadByEventId = {},
  analyticsSurface = "event_list",
  analyticsListType = "unknown",
  flattenHostedView = false,
  datetimeDisplayMode = "date_time",
  titleMaxWidthClass,
  useSearchListLayout = !fromMap,
  enableForYouEnhancements = true,
  separatePlatformEvents = false,
  professionalMeetups = [],
  emphasizeMeetupCards = false,
  highlightedEvents = [],
  onConfirmAttendance = null,
  onJoinSession = null,
  conceptPostLayout = false,
  isConceptFeed = false,
  showTeamConcepts = false,
  onOpenConceptAuthor = null,
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation("events");
  const safeEvents = Array.isArray(events)
    ? events.filter(isRenderableEvent)
    : [];
  const safeFavouriteIds =
    favouriteIds && typeof favouriteIds.has === "function"
      ? favouriteIds
      : new Set(Array.isArray(favouriteIds) ? favouriteIds : []);
  const safeProfessionalMeetups = Array.isArray(professionalMeetups)
    ? professionalMeetups.filter(isRenderableEvent)
    : [];
  const safeHighlightedEvents = Array.isArray(highlightedEvents)
    ? highlightedEvents.filter((ev) => isRenderableEvent(ev) && !safeProfessionalMeetups.some((meetup) => meetup.id === ev.id))
    : [];

  /* ───── после выбора карточки докручиваем, если она перекрыта модалкой ───── */
  useEffect(() => {
    if (!selectedEventId || !modalHeight) return;

    const cardEl = document.getElementById(`event-${selectedEventId}`);
    if (!cardEl) return;

    const scrollEl = getScrollParent(cardEl);
    const gap = 8;

    const cardRect = cardEl.getBoundingClientRect();
    const scrollRect =
      scrollEl === window
        ? { top: 0, bottom: window.innerHeight }
        : scrollEl.getBoundingClientRect();

    const visibleTop = scrollRect.top + gap;
    const visibleBottom = scrollRect.bottom - modalHeight - gap;

    // Если карточка выше видимой зоны — прокручиваем вверх
    if (cardRect.top < visibleTop) {
      const deltaUp = cardRect.top - visibleTop;
      (scrollEl === window ? window : scrollEl).scrollBy({
        top: deltaUp,
        behavior: "smooth",
      });
    }
    // Если карточка перекрыта модалкой снизу — прокручиваем вниз
    else if (cardRect.bottom > visibleBottom) {
      const deltaDown = cardRect.bottom - visibleBottom;
      (scrollEl === window ? window : scrollEl).scrollBy({
        top: deltaDown,
        behavior: "smooth",
      });
    }
  }, [selectedEventId, modalHeight]);

  const [expandedSeriesById, setExpandedSeriesById] = useState({});
  const [timeTick, setTimeTick] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeTick(Date.now());
    }, 60000);
    return () => window.clearInterval(timer);
  }, []);

  if (!hideMapButton && safeEvents.length === 0 && safeHighlightedEvents.length === 0 && safeProfessionalMeetups.length === 0)
    return (
      <div className="text-center text-white mt-10">
        {t("events.list.no_events")}
      </div>
    );

  // ─── динамический нижний "хвост" для мобильного ────────────────
  const mobileBase = "var(--bottom-nav-clearance, 112px)";

  const bottomPadding = isDesktop
    ? "0.5rem"
    : modalHeight > 0
        // открыт сниппет → добавляем его высоту + 8 px зазор
        ? `calc(${mobileBase} + ${modalHeight + 8}px)`
        // сниппет закрыт → опираемся только на базовый отступ (если просили)
        : showBottomMargin
            ? `calc(${mobileBase} + 8px)`
            : "0";

  const highlightedEventIds = new Set(
    safeHighlightedEvents.map((ev) => ev.id).filter(Boolean)
  );
  const professionalMeetupIds = new Set(safeProfessionalMeetups.map((ev) => ev.id));
  const sourceEvents =
    safeEvents.filter((ev) => !highlightedEventIds.has(ev?.id) && !professionalMeetupIds.has(ev?.id));

  // Client-side sorting (used mainly for For You). For Created/Joined/Liked we can preserve backend order.
  const sortEvents = (items) => {
    if (disableClientSort) return items;

    return [...items].sort((a, b) => {
      // Always keep series/multi-events at the top of the list.
      const hasSeriesA =
        enableForYouEnhancements &&
        Array.isArray(a?.child_events) &&
        a.child_events.length > 0;
      const hasSeriesB =
        enableForYouEnhancements &&
        Array.isArray(b?.child_events) &&
        b.child_events.length > 0;
      if (hasSeriesA !== hasSeriesB) return hasSeriesB - hasSeriesA;

      // Promote featured (promoted) events to the very top (For You feed behaviour)
      const isPromotedA = enableForYouEnhancements
        ? !!(a.is_promoted_active ?? a.is_promoted ?? a.promoted)
        : false;
      const isPromotedB = enableForYouEnhancements
        ? !!(b.is_promoted_active ?? b.is_promoted ?? b.promoted)
        : false;
      if (isPromotedA !== isPromotedB) return isPromotedB - isPromotedA; // true first

      // Normalize status strings to lowercase
      const rawA = (eventStatuses?.[a.id] ?? a.status) || "";
      const rawB = (eventStatuses?.[b.id] ?? b.status) || "";
      const statusA = String(rawA).toLowerCase();
      const statusB = String(rawB).toLowerCase();

      // Required priority:
      // 0) awaiting validation
      // 1) active + postponed (+ pending treated as active)
      // 2) finished
      // 3) deleted
      // 4) never-published archive
      // 5) any other
      const getPriority = (ev, status) => {
        if (status === "expired_unvalidated") return 4;
        if (!ev.is_validated) return 0;
        if (["active", "pending", "postponed"].includes(status)) return 1;
        if (status === "finished") return 2;
        if (status === "deleted") return 3;
        return 5;
      };

      const pa = getPriority(a, statusA);
      const pb = getPriority(b, statusB);
      if (pa !== pb) return pa - pb;

      // Within the same group, if both are promoted, sort by promoted_rank ascending
      if (isPromotedA && isPromotedB) {
        const ra = Number.isFinite(a.promoted_rank)
          ? a.promoted_rank
          : Number.MAX_SAFE_INTEGER;
        const rb = Number.isFinite(b.promoted_rank)
          ? b.promoted_rank
          : Number.MAX_SAFE_INTEGER;
        if (ra !== rb) return ra - rb;
      }

      const da = new Date(a.date_time);
      const db = new Date(b.date_time);

      // For finished/deleted groups: newest first (desc)
      if (pa === 2 || pa === 3 || pa === 4) {
        return db - da;
      }

      // For awaiting/active groups: soonest first (asc)
      return da - db;
    });
  };

  const shouldSeparatePlatformEvents =
    !flattenHostedView && (separatePlatformEvents || (enableForYouEnhancements && showMainEvent));
  const nonPlatformEvents = shouldSeparatePlatformEvents
    ? sourceEvents.filter((ev) => !ev?.is_platform_event)
    : sourceEvents;
  const platformSourceEvents = shouldSeparatePlatformEvents
    ? sourceEvents.filter((ev) => !!ev?.is_platform_event)
    : [];
  const mapConceptOnly =
    fromMap &&
    nonPlatformEvents.length > 0 &&
    nonPlatformEvents.every((ev) => ev.type === "concept");
  const useCompactMapListLayout = fromMap && useSearchListLayout;

  // Для обычного списка concepts живут отдельным блоком внизу.
  // Для карты EventList получает уже разрезанные наборы, поэтому в concept-tab
  // концепты должны идти как основной список, а не исчезать.
  const conceptEvents = flattenHostedView
    ? []
    : fromMap
    ? []
    : nonPlatformEvents.filter((ev) => ev.type === "concept");

  const regularEvents = flattenHostedView
    ? nonPlatformEvents
    : fromMap
    ? (mapConceptOnly
        ? nonPlatformEvents.filter((ev) => ev.type === "concept")
        : nonPlatformEvents.filter((ev) => ev.type !== "concept"))
    : nonPlatformEvents.filter((ev) => ev.type !== "concept");

  const sortedRegularEvents = sortEvents(regularEvents);
  const sortedConceptEvents = fromMap ? [] : sortEvents(conceptEvents);
  const hostedConceptEvents = sortedConceptEvents.filter(
    (ev) => !ev?.is_hostless_concept
  );
  const hostlessConceptEvents = sortedConceptEvents.filter(
    (ev) => !!ev?.is_hostless_concept
  );
  const interleavedConceptEvents = (() => {
    if (fromMap) return [];
    const mixed = [];
    const maxLen = Math.max(
      hostedConceptEvents.length,
      hostlessConceptEvents.length
    );
    for (let i = 0; i < maxLen; i += 1) {
      if (hostedConceptEvents[i]) mixed.push(hostedConceptEvents[i]);
      if (hostlessConceptEvents[i]) mixed.push(hostlessConceptEvents[i]);
    }
    return mixed;
  })();

  // Унифицированный рендер карточки события
  const renderEventRow = (
    ev,
    analyticsSection = "default",
    analyticsPosition = 0,
    depth = 0,
    rowOptions = {}
  ) => {
    if (!isRenderableEvent(ev)) return null;

    const fmt =
      datetimeDisplayMode === "date_only"
        ? formatDateOnlyForCard(ev.date_time)
        : formatDatetimeForCard(ev.date_time);
    const status = eventStatuses?.[ev.id] ?? ev.status;
    const unreadCount = showUnreadBadges
      ? Number(unreadByEventId?.[String(ev.id)] ?? ev?.waitlist_unread_count ?? 0) || 0
      : 0;

    const childEvents =
      flattenHostedView || !enableForYouEnhancements
        ? []
        : Array.isArray(ev.child_events)
        ? ev.child_events.filter(isRenderableEvent)
        : [];
    const showSeriesToggle = !flattenHostedView && depth === 0 && childEvents.length > 0;
    const isSeriesExpanded = showSeriesToggle
      ? expandedSeriesById[ev.id] ?? true
      : false;
    const isSelectedSeriesChild = showSeriesToggle
      ? childEvents.some((childEv) => childEv.id === selectedEventId)
      : false;
    const isPlatformEvent = emphasizeMeetupCards || (flattenHostedView ? false : Boolean(ev.is_platform_event));
    const showConceptPost = conceptPostLayout && analyticsSection === "concept" &&
      ev.type === "concept" && !ev.is_platform_event;
    const showMultiEventBadge = enableForYouEnhancements && showSeriesToggle;
    const showPromoted = enableForYouEnhancements && !flattenHostedView && !showMultiEventBadge && !isPlatformEvent
      ? !!(ev.is_promoted_active ?? ev.is_promoted ?? ev.promoted)
      : false;
    const expandableContent = showSeriesToggle ? (
      <div className="space-y-0">
        <div className="flex items-center my-2.5">
          <div className="flex-grow h-px bg-white/10" />
        </div>
        {childEvents.map((childEv, childIndex) => (
          <div key={childEv.id}>
            {childIndex > 0 ? <div className="my-2.5 border-t border-white/10" /> : null}
            <InlineSeriesEventRow
              event={childEv}
              isFavourite={safeFavouriteIds.has(childEv.id)}
              onSelect={onSelect}
              onToggleFavourite={onToggleFavourite}
              datetimeDisplayMode={datetimeDisplayMode}
              titleMaxWidthClass={titleMaxWidthClass}
            />
          </div>
        ))}
      </div>
    ) : null;
    const href = buildEventPath(ev.id, ev.name);
    const handleOpen = (event) => {
      if (!isPlainLeftClick(event)) return;
      event.preventDefault();
      onSelect(ev);
    };

    return (
      <div
        key={ev.id}
        className={`relative overflow-visible ${fromMap ? "" : "mx-[2px]"} ${showSeriesToggle ? "pb-5" : ""} ${showConceptPost && !rowOptions.isLastConceptPost ? "mb-1 border-b border-white/40 pb-6" : ""}`}
      >
        {showConceptPost ? <ConceptPostIntro event={ev} onOpenAuthor={onOpenConceptAuthor} /> : null}
        <EventRowAnchor
          event={ev}
          href={href}
          onOpen={handleOpen}
          analyticsSurface={analyticsSurface}
          analyticsListType={analyticsListType}
          analyticsSection={analyticsSection}
          analyticsPosition={analyticsPosition}
        >
          <EventCard
            title={ev.name}
            type={ev.type}
            isGhostConcept={Boolean(ev?.is_hostless_concept)}
            isOnline={Number(ev.event_format) === 0}
            hideDefaultConceptIcons={isConceptAwaitingDetails(ev)}
            isPersonal={Number(ev.host_format) === 0}
            isPaid={Number(ev.fee_type) === 1}
            isByRequest={Boolean(ev.join_by_request)}
            isVerified={ev.is_verified}
            isValidated={ev.is_validated}
            datetime={fmt}
            isFavourite={safeFavouriteIds.has(ev.id)}
            isSelected={ev.id === selectedEventId || isSelectedSeriesChild}
            fromMap={fromMap}
            onToggleFavourite={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavourite(ev.id);
            }}
            showFavouriteButton={showFavouriteButton}
            status={status}
            badgeLabel={ev.badge_label}
            iconImageUrl={ev.icon_image_url}
            isPromoted={showPromoted}
            isPlatformEvent={isPlatformEvent}
            isCurrentEvent={analyticsSection === "current_event"}
            attention={emphasizeMeetupCards && !ev.is_participant}
            platformAccentIndex={isPlatformEvent ? analyticsPosition : 0}
            unreadCount={unreadCount}
            topCenterBadge={
              rowOptions.topCenterBadge || (showMultiEventBadge ? t("event_card.multi_event_label") : null)
            }
            topCenterBadgeClassName={rowOptions.topCenterBadgeClassName || ""}
            topCenterBadgePlacement={rowOptions.topCenterBadgePlacement || "center"}
            showSeriesToggle={showSeriesToggle}
            isSeriesExpanded={isSeriesExpanded}
            expandableContent={expandableContent}
            onToggleSeriesExpand={() =>
              setExpandedSeriesById((prev) => ({
                ...prev,
                [ev.id]: !prev[ev.id],
              }))
            }
            datetimeDisplayMode={datetimeDisplayMode}
            titleMaxWidthClass={titleMaxWidthClass}
            searchListLayout={useSearchListLayout}
          />
          {Number(ev.distance_from_selected_city_km) > 50 && Number(ev.event_format) !== 0 ? (
            <span className="mt-1 inline-flex rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-white/75">
              {t("event_card.out_of_town_distance", {
                distance: ev.distance_from_selected_city_km,
              })}
            </span>
          ) : null}
        </EventRowAnchor>
      </div>
    );
  };

  const platformEvents = (() => {
    if (!shouldSeparatePlatformEvents) return [];
    const sortedPlatformItems = sortEvents(platformSourceEvents).filter((ev) => {
      if (!ev?.is_platform_event || !ev?.date_time) return false;
      const dt = new Date(ev.date_time);
      return !Number.isNaN(dt.getTime()) && dt.getTime() >= timeTick;
    });
    const platformRegularEvents = sortedPlatformItems.filter((ev) => ev.type !== "concept");
    const platformConceptEvents = sortedPlatformItems.filter((ev) => ev.type === "concept");
    return [...platformRegularEvents, ...platformConceptEvents];
  })();
  const spotlightEvent = !fromMap && shouldSeparatePlatformEvents
    ? platformEvents.find((ev) => ev.is_discovery_spotlight && ev.type !== "concept")
    : null;
  const teamEvents = spotlightEvent
    ? platformEvents.filter((ev) => ev.id !== spotlightEvent.id)
    : platformEvents;

  const regularEventsWithoutPlatform = sortedRegularEvents;
  const nextRegularEvent = regularEventsWithoutPlatform.find((ev) => {
    if (!ev?.date_time) return false;
    const dt = new Date(ev.date_time);
    return !Number.isNaN(dt.getTime()) && dt.getTime() >= timeTick;
  });
  const platformSectionDividerLabel = teamEvents.length === 1 && nextRegularEvent?.date_time
    ? `${t("list.platform_events_divider_prefix", "Starts in")} ${formatStartsIn(
        new Date(nextRegularEvent.date_time),
        t("list.main_event_timer_fallback", "Don't miss it")
      )}`
    : "";
  const hasTeamConcepts = teamEvents.some((ev) => ev.type === "concept");
  const hasTeamRegularEvents = Boolean(spotlightEvent) || teamEvents.some((ev) => ev.type !== "concept");
  const platformEventsLabel = isConceptFeed
    ? t("list.platform_concepts_header", "VaultGrid Team Concepts")
    : showTeamConcepts && hasTeamConcepts
      ? hasTeamRegularEvents
        ? t("list.platform_mixed_header", "VaultGrid Team Events and Concepts")
        : t("list.platform_concepts_header", "VaultGrid Team Concepts")
    : teamEvents.length === 1
      ? t("list.platform_event_header", { defaultValue: "VaultGrid Team Event" })
      : t("list.platform_events_header", "VaultGrid Team Events");
  const platformBadgeBaseClass =
    "!h-[20px] !min-w-[151px] !rounded-b-none !rounded-t-[16px] !px-5 !py-0 !text-[12px] !font-semibold !leading-[20px] !tracking-[0.02em]";
  const getPlatformBadgeClassName = (ev) =>
    ev.id === selectedEventId
      ? `${platformBadgeBaseClass} !border-[#3b82f6] !bg-[#3b82f6] !text-white`
      : `${platformBadgeBaseClass} !border-white !bg-white !text-[#3e6ffb]`;

  const sectionClassName = useCompactMapListLayout
    ? "overflow-visible px-[1px]"
    : "overflow-visible px-[3px]";
  const listGapClass = "gap-[16px]";
  const platformEventsGapClass = useCompactMapListLayout
    ? "gap-[24px]"
    : fromMap
      ? "gap-[16px]"
      : "gap-[14px]";
  const showPlatformBadgeOnCard = !fromMap || useCompactMapListLayout;
  const platformFeedsDirectlyIntoConcepts =
    teamEvents.length > 0 &&
    regularEventsWithoutPlatform.length === 0 &&
    !flattenHostedView &&
    !fromMap &&
    interleavedConceptEvents.length > 0;

  return (
    <section className={sectionClassName} style={{ paddingBottom: bottomPadding }}>
      <div className={`flex flex-col ${listGapClass} overflow-visible`}>
        {spotlightEvent ? (
          <div className="pt-3" data-event-spotlight>
            <EventRowAnchor
              event={spotlightEvent}
              href={buildEventPath(spotlightEvent.id, spotlightEvent.name)}
              onOpen={(event) => {
                if (!isPlainLeftClick(event)) return;
                event.preventDefault();
                onSelect?.(spotlightEvent);
              }}
              analyticsSurface={analyticsSurface}
              analyticsListType={analyticsListType}
              analyticsSection="discovery_spotlight"
              analyticsPosition={0}
            >
              <div className="overflow-hidden rounded-[24px] border border-amber-200/70 bg-[#10162b] text-white shadow-[0_14px_38px_rgba(5,9,24,0.38),0_0_22px_rgba(251,191,36,0.18)] transition-transform active:scale-[0.99]">
                <div className="relative flex h-[310px] items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_28%,#4a3157_0%,#182341_58%,#10162b_100%)]">
                  {(spotlightEvent.hero_image_url || spotlightEvent.icon_image_url) ? (
                    <>
                      <img
                        src={spotlightEvent.hero_image_url || spotlightEvent.icon_image_url}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-30 blur-xl"
                        loading="lazy"
                      />
                      <img
                        src={spotlightEvent.hero_image_url || spotlightEvent.icon_image_url}
                        alt=""
                        className="relative h-full w-full object-contain"
                        loading="lazy"
                      />
                    </>
                  ) : null}
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#10162b] to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full border border-amber-200/70 bg-[#11182c]/85 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.1em] text-amber-100 backdrop-blur-sm">
                    {t("list.spotlight_header", { defaultValue: "VaultGrid highlight" })}
                  </span>
                </div>
                <div className="px-4 pb-4 pt-1">
                  <div className="text-[22px] font-bold leading-tight">{spotlightEvent.name}</div>
                  {spotlightEvent.date_time ? (
                    <div className="mt-1 text-[13px] font-medium text-white/75">
                      {formatDatetimeForCard(spotlightEvent.date_time)}
                      {spotlightEvent.city ? ` · ${spotlightEvent.city}` : ""}
                    </div>
                  ) : null}
                  <div className="mt-4 flex items-center justify-center rounded-[14px] bg-[#f4bd52] px-4 py-3 text-[15px] font-bold text-[#1b2236]">
                    {t("list.spotlight_view_event", { defaultValue: "View event" })}
                    <span aria-hidden="true" className="ml-2">→</span>
                  </div>
                </div>
              </div>
            </EventRowAnchor>
          </div>
        ) : null}
        {safeProfessionalMeetups.length > 0 ? (
          <>
            <div className={`flex flex-col overflow-visible pt-[32px] ${platformEventsGapClass}`} data-professional-meetup-section>
              {safeProfessionalMeetups.map((ev, index) =>
                renderEventRow(ev, "professional_meetup", index, 0, {
                  topCenterBadge: index === 0
                    ? t("list.professional_meetup_header", { defaultValue: "Your Professional Meetup" })
                    : null,
                  topCenterBadgePlacement: "above",
                  topCenterBadgeClassName: getPlatformBadgeClassName(ev),
                })
              )}
            </div>
            {(safeHighlightedEvents.length > 0 || sourceEvents.length > 0) ? <SectionSeparator /> : null}
          </>
        ) : null}
        {safeHighlightedEvents.length > 0 ? (
          <>
            <div className="flex flex-col gap-4 overflow-visible pt-[32px] mb-2">
              {safeHighlightedEvents.map((ev, idx) => {
                const isActiveEvent = String(ev?.status || "").toLowerCase() === "active";
                const viewerConfirmed = hasViewerConfirmedAttendance(ev);
                const canJoinSession =
                  isActiveEvent && supportsPartySession(ev) && viewerConfirmed;
                const canConfirmAttendance = isActiveEvent && !viewerConfirmed;

                return (
                  <div key={ev.id}>
                    {idx > 0 ? <div className="my-2.5 border-t border-emerald-200/20" /> : null}
                    {renderEventRow(ev, "current_event", -1 - idx, 0, {
                      topCenterBadge: t("list.current_event_header"),
                      topCenterBadgePlacement: "above",
                      topCenterBadgeClassName: `${platformBadgeBaseClass} vg-current-event-accent !border-emerald-400 !text-white`,
                    })}
                    {canJoinSession ? (
                      <button
                        type="button"
                        onClick={() => onJoinSession?.(ev)}
                        className="vg-attendance-cta-shimmer mt-3 w-full rounded-[999px] px-6 py-2.5 text-[16px] font-semibold text-white shadow-[0_8px_18px_rgba(16,185,129,0.38)] active:scale-[0.99] whitespace-nowrap"
                      >
                        {t("events.details.join_session", { defaultValue: "Join party" })}
                      </button>
                    ) : canConfirmAttendance ? (
                      <button
                        type="button"
                        onClick={() => onConfirmAttendance?.(ev)}
                        className="vg-attendance-cta-shimmer mt-3 w-full rounded-[999px] px-6 py-2.5 text-[16px] font-semibold text-white shadow-[0_8px_18px_rgba(16,185,129,0.38)] active:scale-[0.99] whitespace-nowrap"
                      >
                        {t("events.details.confirm_attendance", {
                          defaultValue: "Confirm arrival",
                        })}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {sourceEvents.length > 0 ? <SectionSeparator /> : null}
          </>
        ) : null}
        {teamEvents.length > 0 && (
          <>
            {fromMap && !useCompactMapListLayout ? (
              <div className="flex items-center mt-1 mb-0">
                <div className="flex-1 h-px bg-white/25" />
                <span className="mx-3 text-xs font-semibold tracking-[0.16em] uppercase text-white/75">
                  {platformEventsLabel}
                </span>
                <div className="flex-1 h-px bg-white/25" />
              </div>
            ) : (
              <div className="h-[6px]" />
            )}
            <div className={`flex flex-col overflow-visible ${platformEventsGapClass}`}>
              {teamEvents.map((ev, index) =>
                renderEventRow(ev, "platform_event", index, 0, {
                  topCenterBadge: index === 0 && showPlatformBadgeOnCard ? platformEventsLabel : null,
                  topCenterBadgePlacement: "above",
                  topCenterBadgeClassName:
                    index === 0 && showPlatformBadgeOnCard
                      ? getPlatformBadgeClassName(ev)
                      : "",
                })
              )}
            </div>
            {regularEventsWithoutPlatform.length > 0 ? (
              <SectionSeparator label={platformSectionDividerLabel} />
            ) : platformFeedsDirectlyIntoConcepts && isConceptFeed ? (
              <SectionSeparator />
            ) : platformFeedsDirectlyIntoConcepts ? null : (
              <div className="h-2" />
            )}
          </>
        )}

        {flattenHostedView || fromMap || interleavedConceptEvents.length === 0 ? (
          regularEventsWithoutPlatform.map((ev, index) =>
            renderEventRow(ev, "regular", index)
          )
        ) : (
          <>
            {regularEventsWithoutPlatform.map((ev, index) =>
              renderEventRow(ev, "regular", index)
            )}

            {isConceptFeed ? (
              regularEventsWithoutPlatform.length > 0 ? <SectionSeparator /> : null
            ) : (
              <ConceptsDivider
                label={t("list.concepts", "Concepts")}
                compact={platformFeedsDirectlyIntoConcepts}
              />
            )}

            {interleavedConceptEvents.map((ev, index) =>
              renderEventRow(ev, "concept", sortedRegularEvents.length + index, 0, {
                isLastConceptPost: index === interleavedConceptEvents.length - 1,
              })
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default EventList;
